import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../mock';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, projectType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Anfrage gesendet!",
          description: "Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      } else {
        throw new Error('Fehler beim Senden');
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Es gab ein Problem beim Senden Ihrer Anfrage. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    {
      id: 1,
      icon: MapPin,
      title: 'Adresse',
      content: companyInfo.address,
      link: `https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`
    },
    {
      id: 2,
      icon: Phone,
      title: 'Telefon',
      content: companyInfo.phone,
      link: `tel:${companyInfo.phone.replace(/\\s/g, '')}`
    },
    {
      id: 3,
      icon: Mail,
      title: 'E-Mail',
      content: companyInfo.email,
      link: `mailto:${companyInfo.email}`
    },
    {
      id: 4,
      icon: Clock,
      title: 'Öffnungszeiten',
      content: companyInfo.openingHours
    }
  ];

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* Diagonal Background Design */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#E6F7FF] to-white"></div>
        <div className="absolute bottom-0 right-0 w-2/3 h-2/3 bg-gradient-to-tl from-[#004d99]/5 to-transparent transform skew-y-6"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#004d99] to-[#00CCDD] text-white px-6 py-2 rounded-full mb-6">
            <Send className="w-4 h-4" />
            <span className="font-semibold text-sm uppercase tracking-wide">Kontakt</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#003366] mb-6">
            Lassen Sie uns über Ihr <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#004d99] to-[#00CCDD]">Projekt</span> sprechen
          </h2>
          <p className="text-xl text-gray-600">
            Nehmen Sie Kontakt mit uns auf – Wir freuen uns auf Ihre Anfrage!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Contact Form */}
          <div className="relative">
            {/* Diagonal accent behind form */}
            <div className="absolute -top-6 -left-6 w-full h-full bg-gradient-to-br from-[#004d99] to-[#00CCDD] rounded-3xl opacity-5 transform rotate-2"></div>
            
            <div className="relative bg-white rounded-3xl shadow-2xl p-8 sm:p-10 border-2 border-[#00CCDD]/20">
              <h3 className="text-2xl font-bold text-[#003366] mb-6">Anfrage senden</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Ihr Name"
                    className="w-full border-2 border-gray-200 focus:border-[#00CCDD] rounded-xl"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="ihre@email.de"
                      className="w-full border-2 border-gray-200 focus:border-[#00CCDD] rounded-xl"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefon
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0176 12345678"
                      className="w-full border-2 border-gray-200 focus:border-[#00CCDD] rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Projektart *
                  </label>
                  <Select onValueChange={handleSelectChange} value={formData.projectType} required>
                    <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#00CCDD] rounded-xl">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sanierung">Sanierung</SelectItem>
                      <SelectItem value="neubau">Neubau / Schlüsselfertig</SelectItem>
                      <SelectItem value="innenausbau">Innenausbau</SelectItem>
                      <SelectItem value="planung">Planung & Beratung</SelectItem>
                      <SelectItem value="sonstiges">Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nachricht *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Beschreiben Sie Ihr Projekt..."
                    rows={5}
                    className="w-full border-2 border-gray-200 focus:border-[#00CCDD] rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#004d99] to-[#00CCDD] hover:from-[#003366] hover:to-[#004d99] text-white py-6 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 group"
                >
                  {isSubmitting ? (
                    'Wird gesendet...'
                  ) : (
                    <>
                      Anfrage absenden
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Right: Contact Info & Map */}
          <div className="space-y-6">
            {/* Contact Details Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {contactDetails.map((detail) => {
                const IconComponent = detail.icon;
                const rotation = detail.id % 2 === 0 ? 'rotate-1' : '-rotate-1';
                
                return (
                  <div key={detail.id} className={`group ${rotation} hover:rotate-0 transition-all duration-300`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#00CCDD] h-full">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-[#004d99] to-[#00CCDD] flex items-center justify-center group-hover:scale-110 transition-transform">
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-[#003366] mb-1 text-sm uppercase tracking-wide">{detail.title}</p>
                          {detail.link ? (
                            <a
                              href={detail.link}
                              target={detail.icon === MapPin ? '_blank' : undefined}
                              rel={detail.icon === MapPin ? 'noopener noreferrer' : undefined}
                              className="text-gray-600 hover:text-[#00CCDD] transition-colors font-medium"
                            >
                              {detail.content}
                            </a>
                          ) : (
                            <p className="text-gray-600 font-medium">{detail.content}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Google Maps */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              {/* Diagonal frame accent */}
              <div className="absolute -top-4 -right-4 w-full h-full border-4 border-[#00CCDD] rounded-3xl pointer-events-none z-10 transform rotate-2"></div>
              
              <div className="relative h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2515.4!2d7.0875!3d50.7333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bf3e5e5c5e5e5f%3A0x5e5e5e5e5e5e5e5e!2sBornheimer%20Str.%20127%2C%2053119%20Bonn!5e0!3m2!1sde!2sde!4v1620000000000!5m2!1sde!2sde"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="THS Bauprojekte GmbH Standort"
                ></iframe>
              </div>
            </div>

            {/* Quick Contact CTA */}
            <div className="bg-gradient-to-br from-[#003366] to-[#004d99] rounded-3xl p-8 text-white relative overflow-hidden">
              {/* Decorative diagonal */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 transform rotate-45 translate-x-16 -translate-y-16"></div>
              
              <div className="relative">
                <h4 className="text-2xl font-bold mb-4">Dringend? Rufen Sie uns an!</h4>
                <p className="text-white/90 mb-6">Montag–Freitag, ab 07:00 Uhr erreichbar</p>
                <a
                  href="tel:017659885691"
                  className="inline-flex items-center bg-white text-[#004d99] px-6 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-xl group"
                >
                  <Phone className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform" />
                  0176 59885691
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};